import LegalLayout from "./components/LegalLayout";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="text-xl font-bold text-faro-navy mb-4 pb-2 border-b border-faro-gray-mid">{title}</h2>
      <div className="text-faro-gray-text text-sm leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

interface CookieRowProps {
  name: string;
  type: string;
  purpose: string;
  duration: string;
}

function CookieRow({ name, type, purpose, duration }: CookieRowProps) {
  return (
    <tr className="border-b border-faro-gray-mid hover:bg-faro-teal-pale/30 transition-colors">
      <td className="py-3 px-4 font-medium text-faro-navy text-xs">{name}</td>
      <td className="py-3 px-4 text-xs">{type}</td>
      <td className="py-3 px-4 text-xs">{purpose}</td>
      <td className="py-3 px-4 text-xs whitespace-nowrap">{duration}</td>
    </tr>
  );
}

export default function PoliticaCookiesPage() {
  return (
    <LegalLayout title="Política de Cookies" lastUpdated="26 de marzo de 2026">
      <Section title="1. ¿Qué son las cookies?">
        <p>
          Las cookies son pequeños archivos de texto que los sitios web almacenan en el dispositivo del usuario cuando
          éste los visita. Permiten que el sitio web recuerde las acciones y preferencias del usuario durante un
          período de tiempo, de modo que no tenga que volver a introducirlas cuando regrese al sitio o navegue
          de una página a otra.
        </p>
      </Section>

      <Section title="2. Tipos de cookies que utilizamos">
        <p>El sitio web <strong>faroenergia.es</strong> utiliza los siguientes tipos de cookies:</p>

        <div className="overflow-x-auto mt-4">
          <table className="w-full border border-faro-gray-mid rounded-lg overflow-hidden text-left">
            <thead>
              <tr className="bg-faro-navy text-white">
                <th className="py-3 px-4 text-xs font-semibold">Cookie</th>
                <th className="py-3 px-4 text-xs font-semibold">Tipo</th>
                <th className="py-3 px-4 text-xs font-semibold">Finalidad</th>
                <th className="py-3 px-4 text-xs font-semibold">Duración</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              <CookieRow
                name="_faro_session"
                type="Técnica / Sesión"
                purpose="Mantener la sesión del usuario durante la navegación"
                duration="Sesión"
              />
              <CookieRow
                name="_faro_consent"
                type="Técnica"
                purpose="Almacena las preferencias de consentimiento de cookies del usuario"
                duration="12 meses"
              />
              <CookieRow
                name="_ga"
                type="Analítica (Google Analytics)"
                purpose="Distingue usuarios únicos y genera estadísticas de visitas"
                duration="2 años"
              />
              <CookieRow
                name="_ga_*"
                type="Analítica (Google Analytics)"
                purpose="Mantiene el estado de la sesión en Google Analytics 4"
                duration="2 años"
              />
              <CookieRow
                name="_gid"
                type="Analítica (Google Analytics)"
                purpose="Distingue usuarios; se actualiza cada 24 horas"
                duration="24 horas"
              />
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="3. Clasificación por finalidad">
        <div className="space-y-4">
          <div className="bg-faro-teal-pale rounded-lg p-4">
            <h3 className="font-semibold text-faro-navy mb-1">Cookies técnicas (necesarias)</h3>
            <p>
              Son imprescindibles para el funcionamiento del sitio web. Sin ellas, el sitio no puede funcionar
              correctamente. No requieren el consentimiento del usuario.
            </p>
          </div>
          <div className="bg-faro-teal-pale rounded-lg p-4">
            <h3 className="font-semibold text-faro-navy mb-1">Cookies analíticas</h3>
            <p>
              Permiten cuantificar el número de usuarios y realizar la medición estadística de la actividad del
              sitio web. Analizan la navegación para mejorar el servicio. Requieren el consentimiento del usuario.
            </p>
          </div>
        </div>
      </Section>

      <Section title="4. Base legal para el uso de cookies">
        <p>
          El uso de cookies técnicas se basa en el <strong>interés legítimo</strong> de Faro Energía para el
          correcto funcionamiento del sitio web (art. 6.1.f del RGPD).
        </p>
        <p>
          El uso de cookies analíticas y de seguimiento se basa en el <strong>consentimiento expreso</strong> del
          usuario (art. 6.1.a del RGPD), que puede otorgarse o revocarse en cualquier momento a través del
          panel de preferencias de cookies.
        </p>
      </Section>

      <Section title="5. Gestión y desactivación de cookies">
        <p>
          El usuario puede configurar sus preferencias de cookies en cualquier momento haciendo clic en el
          enlace <strong>"Configurar cookies"</strong> disponible en el banner de cookies o en el pie de página.
        </p>
        <p>Además, puede gestionar las cookies directamente desde su navegador:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li><strong className="text-faro-navy">Google Chrome:</strong> Configuración &gt; Privacidad y seguridad &gt; Cookies y otros datos de sitios</li>
          <li><strong className="text-faro-navy">Mozilla Firefox:</strong> Opciones &gt; Privacidad y seguridad &gt; Cookies y datos del sitio</li>
          <li><strong className="text-faro-navy">Safari:</strong> Preferencias &gt; Privacidad &gt; Cookies y datos de sitios web</li>
          <li><strong className="text-faro-navy">Microsoft Edge:</strong> Configuración &gt; Privacidad, búsqueda y servicios &gt; Cookies</li>
        </ul>
        <p>
          La desactivación de cookies puede afectar al correcto funcionamiento de algunas funcionalidades del
          sitio web.
        </p>
      </Section>

      <Section title="6. Cookies de terceros">
        <p>
          Este sitio web puede utilizar servicios de terceros que instalan cookies propias. En particular, se
          utilizan los servicios de análisis de <strong>Google Analytics</strong> (Google LLC). Puede consultar
          la política de privacidad de Google en <a href="https://policies.google.com/privacy" rel="nofollow" className="text-faro-teal underline hover:text-faro-navy transition-colors">policies.google.com/privacy</a>.
        </p>
        <p>
          Para inhabilitar el seguimiento de Google Analytics, puede instalar el complemento oficial:&nbsp;
          <a href="https://tools.google.com/dlpage/gaoptout" rel="nofollow" className="text-faro-teal underline hover:text-faro-navy transition-colors">tools.google.com/dlpage/gaoptout</a>.
        </p>
      </Section>

      <Section title="7. Actualizaciones de la política de cookies">
        <p>
          Faro Energía puede modificar esta Política de Cookies en función de exigencias legislativas o
          reglamentarias, o con el fin de adaptar dicha política a las instrucciones dictadas por la Agencia
          Española de Protección de Datos. Cuando se produzcan cambios significativos, se informará al usuario
          mediante un nuevo aviso en el sitio web.
        </p>
      </Section>

      <Section title="8. Contacto">
        <p>
          Para cualquier consulta relacionada con el uso de cookies en este sitio web, puede ponerse en contacto
          con Faro Energía a través del correo electrónico <strong>info@faroenergia.es</strong>.
        </p>
      </Section>
    </LegalLayout>
  );
}
